import {schemaTypeToString, unknownToString} from './unknownToString';
// add needed utils here
export const falsyArray = [null, undefined, ''];

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export const isFalsy = (key: unknown) =>
  falsyArray.includes(key as null | string | undefined);

export type InputFieldType =
  | 'String'
  | 'Number'
  | 'Boolean'
  | 'Object'
  | 'Array';

export function inferInputFieldType(value: unknown): InputFieldType {
  if (Array.isArray(value)) return 'Array';
  if (value instanceof Date) return 'String';
  if (value === null || value === undefined) return 'String';
  if (typeof value === 'object') return 'Object';
  if (typeof value === 'number') return 'Number';
  if (typeof value === 'boolean') return 'Boolean';
  return 'String';
}

function coerceNumberValue(value: unknown): unknown {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function coerceBooleanValue(value: unknown): unknown {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return Boolean(value);
}

function coerceObjectValue(value: unknown): unknown {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};
}

export function coerceInputFieldValue(
  type: InputFieldType,
  value: unknown
): unknown {
  switch (type) {
    case 'Number':
      return coerceNumberValue(value);
    case 'Boolean':
      return coerceBooleanValue(value);
    case 'String':
      return value === null || value === undefined
        ? ''
        : unknownToString(value);
    case 'Array':
      return Array.isArray(value) ? value : [];
    case 'Object':
      return coerceObjectValue(value);
    default:
      return value;
  }
}

function sampleArrayTemplate(arr: unknown[]): unknown[] {
  if (arr.length === 0) return [];
  const first = arr[0];
  if (first instanceof Date) return [first.toISOString()];
  if (Array.isArray(first)) return [];
  if (first && typeof first === 'object') {
    return [sampleJsonToInputFields(first as Record<string, unknown>)];
  }
  return arr.map((item) =>
    item instanceof Date
      ? item.toISOString()
      : coerceInputFieldValue(inferInputFieldType(item), item)
  );
}

function sampleInputFieldForValue(rawValue: unknown): Record<string, unknown> {
  if (rawValue instanceof Date) {
    return {
      type: 'String',
      value: rawValue.toISOString(),
      required: false,
      enum: [],
      trimSpaces: false,
    };
  }

  const type = inferInputFieldType(rawValue);
  if (type === 'Object') {
    return {
      type: 'Object',
      value: sampleJsonToInputFields(rawValue as Record<string, unknown>),
      required: false,
    };
  }
  if (type === 'Array') {
    return {
      type: 'Array',
      value: sampleArrayTemplate(rawValue as unknown[]),
      required: false,
    };
  }

  return {
    type,
    value: coerceInputFieldValue(type, rawValue),
    required: false,
    enum: [],
    trimSpaces: false,
  };
}

export function sampleJsonToInputFields(
  sample: Record<string, unknown>
): Record<string, unknown> {
  if (!sample || typeof sample !== 'object' || Array.isArray(sample)) {
    return {};
  }

  const result: Record<string, unknown> = {};
  for (const [key, rawValue] of Object.entries(sample)) {
    result[key] = sampleInputFieldForValue(rawValue);
  }
  return result;
}

function toInputFieldType(typeLower: string): InputFieldType {
  switch (typeLower) {
    case 'number':
    case 'integer':
      return 'Number';
    case 'boolean':
      return 'Boolean';
    case 'array':
      return 'Array';
    case 'object':
      return 'Object';
    default:
      return 'String';
  }
}

function getDefaultValue(prop: Record<string, unknown>): unknown {
  if (Object.hasOwn(prop, 'default')) {
    return prop.default;
  } else if (prop.enum) {
    return (prop.enum as unknown[])[0] ?? '';
  } else if (prop.const) {
    return prop.const;
  } else {
    switch (prop.type) {
      case 'string':
        return '';
      case 'number':
      case 'integer':
        return 0;
      case 'boolean':
        return false;
      case 'array':
        return [];
      case 'object':
        return {};
      default:
        return null;
    }
  }
}

function getTypeString(prop: Record<string, unknown>): InputFieldType {
  if (Array.isArray(prop.type)) {
    const primary =
      prop.type.find(
        (t): t is string => typeof t === 'string' && t !== 'null'
      ) ?? 'string';
    return toInputFieldType(primary);
  }

  return toInputFieldType(schemaTypeToString(prop.type));
}

function handleObjectProperty(prop: Record<string, unknown>): unknown {
  return inputSchemaPropertiesToObject(
    prop.properties as Record<string, unknown>,
    (prop.required as string[]) || []
  );
}

function handleArrayProperty(
  prop: Record<string, unknown>,
  required: string[],
  key: string
): unknown {
  const items = (prop.items ?? {}) as Record<string, unknown>;

  if (items.type === 'object' && items.properties) {
    return {
      type: 'Array',
      value: [
        inputSchemaPropertiesToObject(
          items.properties as Record<string, unknown>,
          (items.required as string[]) || []
        ),
      ],
      required: required.includes(key),
    };
  }

  if (Array.isArray(prop.default)) {
    const first = prop.default[0];
    const isInternalFieldShape =
      first &&
      typeof first === 'object' &&
      !Array.isArray(first) &&
      'type' in (first as Record<string, unknown>) &&
      'value' in (first as Record<string, unknown>);

    return {
      type: 'Array',
      value: isInternalFieldShape
        ? prop.default
        : prop.default.map((item) =>
            item instanceof Date
              ? item.toISOString()
              : coerceInputFieldValue(inferInputFieldType(item), item)
          ),
      required: required.includes(key),
      enum: Array.isArray(prop.enum) ? prop.enum : [],
    };
  }

  return {
    value: [],
    required: required.includes(key),
    enum: Array.isArray(prop.enum) ? prop.enum : [],
    type: 'Array',
  };
}

export const inputSchemaPropertiesToObject = (
  properties: Record<string, unknown>,
  requiredFields: string[] = []
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const key in properties) {
    const prop = properties[key] as Record<string, unknown>;

    if (prop.type === 'object') {
      // Recursively build the nested object (empty objects have no properties)
      const nested = handleObjectProperty({
        ...prop,
        properties:
          prop.properties &&
          typeof prop.properties === 'object' &&
          !Array.isArray(prop.properties)
            ? prop.properties
            : {},
      });
      result[key] = {
        value: nested,
        required: requiredFields.includes(key),
        type: 'Object',
      };
      continue;
    }

    if (prop.type === 'array') {
      result[key] = handleArrayProperty(prop, requiredFields, key);
      continue;
    }

    const value = getDefaultValue(prop);
    const typeString = getTypeString(prop);

    result[key] = {
      value: coerceInputFieldValue(typeString, value),
      required: requiredFields.includes(key),
      enum: Array.isArray(prop.enum) ? prop.enum : [],
      trimSpaces: prop.trimSpaces === true,
      type: typeString,
    };
  }
  return result;
};

export const constantPropertiesToObject = (
  properties: Record<string, unknown>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const key in properties) {
    const prop = properties[key] as Record<string, unknown>;

    // Determine value
    let value = '';
    if (Object.hasOwn(prop, 'default')) {
      value = prop.default as string;
    } else if (Object.hasOwn(prop, 'const')) {
      value = prop.const as string;
    }

    result[key] = {
      value,
      type: 'String',
    };
  }

  return result;
};

function handleObjectField(
  field: Record<string, unknown>,
  key: string,
  required: string[],
  processObject: (obj: Record<string, unknown>) => {
    properties: Record<string, unknown>;
    required: string[];
  }
) {
  const schemaField: Record<string, unknown> = {type: 'object'};
  const fieldValue = field.value;

  if (isPlainRecord(fieldValue)) {
    const nested = processObject(fieldValue);
    schemaField.properties = nested.properties;
    if (nested.required.length > 0) {
      schemaField.required = nested.required;
    }
  } else {
    schemaField.properties = {};
  }

  if (field.required) {
    required.push(key);
  }

  return schemaField;
}

function resolveArrayItemSchema(
  fieldValue: unknown[],
  processObject: (obj: Record<string, unknown>) => {
    properties: Record<string, unknown>;
    required: string[];
  }
): Record<string, unknown> {
  // Empty arrays must remain arrays in the API schema. Inferring "string"
  // drops the array type and corrupts fields like tags/documents/custom_fields.
  if (fieldValue.length === 0) {
    return {
      type: 'array',
      items: {type: 'string'},
    };
  }

  const first = fieldValue[0];

  if (Array.isArray(first)) {
    return {
      type: 'array',
      items: resolveArrayItemSchema(first, processObject),
    };
  }

  if (isPlainRecord(first)) {
    const nested = processObject(first);
    const items: Record<string, unknown> = {
      type: 'object',
      properties: nested.properties,
    };
    if (nested.required.length > 0) {
      items.required = nested.required;
    }
    return {type: 'array', items};
  }

  const itemType = inferInputFieldType(first).toLowerCase();
  return {
    type: 'array',
    items: {type: itemType === 'number' ? 'number' : itemType},
  };
}

function handleArrayField(
  field: Record<string, unknown>,
  key: string,
  required: string[],
  processObject: (obj: Record<string, unknown>) => {
    properties: Record<string, unknown>;
    required: string[];
  }
) {
  const fieldValue = Array.isArray(field.value) ? field.value : [];
  const schemaField = resolveArrayItemSchema(fieldValue, processObject);

  if (field.required) {
    required.push(key);
  }

  return schemaField;
}

function buildSchemaField(
  field: Record<string, unknown>,
  key: string,
  required: string[],
  processObject: (obj: Record<string, unknown>) => {
    properties: Record<string, unknown>;
    required: string[];
  }
) {
  const typeLower = schemaTypeToString(field.type).toLowerCase();
  let schemaField: Record<string, unknown> = {
    type: typeLower === 'integer' ? 'number' : typeLower,
  };

  if (typeLower === 'object') {
    schemaField = handleObjectField(field, key, required, processObject);
  } else if (typeLower === 'array') {
    schemaField = handleArrayField(field, key, required, processObject);
  } else {
    const normalizedType = toInputFieldType(typeLower);
    schemaField.type = normalizedType.toLowerCase();
    schemaField.default = coerceInputFieldValue(normalizedType, field.value);
    if (field.required) {
      required.push(key);
    }

    if (Array.isArray(field.enum) && field.enum.length > 0) {
      schemaField.enum = field.enum;
    }

    if (field.trimSpaces === true) {
      schemaField.trimSpaces = true;
    }

    return schemaField;
  }

  return schemaField;
}

function buildLegacyObjectField(
  field: Record<string, unknown>,
  key: string,
  required: string[],
  processObject: (obj: Record<string, unknown>) => {
    properties: Record<string, unknown>;
    required: string[];
  }
) {
  const nested = processObject(field);
  const objField = {
    type: 'object',
    properties: nested.properties,
    ...(nested.required.length > 0 ? {required: nested.required} : {}),
  };
  if (field.required) {
    required.push(key);
  }
  return objField;
}

export const convertObjectToSchema = (
  input: Record<string, unknown>
): Record<string, unknown> => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return {type: 'object', properties: {}};
  }

  function processObject(obj: Record<string, unknown>) {
    const properties: Record<string, unknown> = {};
    const required: string[] = [];

    for (const key in obj) {
      const field = obj[key] as Record<string, unknown>;
      if (
        field &&
        typeof field === 'object' &&
        field !== null &&
        'value' in field &&
        'type' in field
      ) {
        properties[key] = buildSchemaField(field, key, required, processObject);
      } else if (
        field &&
        typeof field === 'object' &&
        field !== null &&
        !Array.isArray(field) &&
        !('value' in field)
      ) {
        properties[key] = buildLegacyObjectField(
          field,
          key,
          required,
          processObject
        );
      } else if (field !== null && field !== undefined) {
        const primitiveType = inferInputFieldType(field);
        properties[key] = {
          type: primitiveType.toLowerCase(),
          default: coerceInputFieldValue(primitiveType, field),
        };
      }
    }
    return {properties, required};
  }

  const result = processObject(input);
  return {
    type: 'object',
    properties: result.properties,
    ...(result.required.length > 0 ? {required: result.required} : {}),
  };
};

// CSV validation: checks for at least one comma and consistent columns
export function isValidCSV(csv: string): boolean {
  const lines = csv.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 1) return false;
  const colCount = lines[0].split(',').length;
  return lines.every((line) => line.split(',').length === colCount);
}

// Convert CSV string to a single JSON object (not array)
export function csvToJson(csv: string): string {
  const lines = csv.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return JSON.stringify({}, null, 2);
  const headers = lines[0].split(',');
  const values = lines[1].split(',');
  const obj: Record<string, string> = {};
  headers.forEach((header, i) => {
    obj[header.trim()] = values[i]?.trim() || '';
  });
  return JSON.stringify(obj, null, 2);
}

// Checks if a string is a valid, non-empty JSON object or a non-empty string
export function isValidSchema(schema: string): boolean {
  if (schema == null || schema === '') return false;
  try {
    const parsed = JSON.parse(schema);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      Object.keys(parsed).length === 0
    ) {
      return false;
    }
    return true;
  } catch {
    return schema.trim() !== '';
  }
}

/**
 * Timezone and date formatting utilities
 */

/**
 * Get the user's current timezone
 * @returns The user's timezone identifier (e.g., 'America/New_York', 'Europe/London')
 */
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Fallback to a default timezone if detection fails
    return 'UTC';
  }
}

/**
 * Convert a date string from GMT/UTC to the user's local timezone and format it
 * @param dateString - The date string to format (assumed to be in GMT/UTC)
 * @param options - Optional formatting options
 * @returns Formatted date string in user's local timezone
 */
function parseDateStringAsUtc(dateString: string): Date {
  if (
    dateString.includes('T') &&
    !dateString.includes('Z') &&
    !dateString.includes('+') &&
    !dateString.includes('-', 10)
  ) {
    return new Date(dateString + 'Z');
  }
  if (!dateString.includes('T') && !dateString.includes(' ')) {
    return new Date(dateString + 'T00:00:00Z');
  }
  return new Date(dateString);
}

function reorderFormattedDate(
  formattedDate: string,
  dateFormat: 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD'
): string {
  if (dateFormat === 'DD-MM-YYYY') return formattedDate;

  const parts = formattedDate.split(/[/,]/);
  if (parts.length < 3) return formattedDate;

  if (dateFormat === 'MM-DD-YYYY') {
    const [day, month] = parts;
    const restOfString = parts.slice(2).join(',');
    return `${month}/${day}/${restOfString}`;
  }

  const [day, month, year] = parts;
  const timepart = parts.length > 3 ? `, ${parts.slice(3).join(',')}` : '';
  return `${year}-${month}-${day}${timepart}`;
}

export function formatDateToUserTimezone(
  dateString: string,
  options: {
    includeTime?: boolean;
    dateFormat?: 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD';
    timeFormat?: '12h' | '24h';
  } = {}
): string {
  const {
    includeTime = true,
    dateFormat = 'DD-MM-YYYY',
    timeFormat = '12h',
  } = options;

  try {
    const date = parseDateStringAsUtc(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const userTimezone = getUserTimezone();
    const dateOptions: Intl.DateTimeFormatOptions = {
      timeZone: userTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };

    if (includeTime) {
      dateOptions.hour = '2-digit';
      dateOptions.minute = '2-digit';
      dateOptions.hour12 = timeFormat === '12h';
    }

    const formatter = new Intl.DateTimeFormat('en-GB', dateOptions);
    return reorderFormattedDate(formatter.format(date), dateFormat);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return 'Unknown date';
  }
}

/**
 * Format a date string with a more user-friendly relative format when appropriate
 * @param dateString - The date string to format
 * @param options - Formatting options
 * @returns Formatted date string with relative time when recent
 */
function formatSameDayRelativeTime(diffInMs: number): string {
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

  if (diffInHours < 1) {
    if (diffInMinutes < 1) {
      return 'Just now';
    }
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  }
  return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
}

function formatRecentRelativeTime(
  diffInMs: number,
  diffInDays: number
): string {
  if (diffInDays === 0) return formatSameDayRelativeTime(diffInMs);
  if (diffInDays === 1) return 'Yesterday';
  return `${diffInDays} days ago`;
}

export function formatDateWithRelativeTime(
  dateString: string,
  options: {
    showRelativeTime?: boolean;
    maxRelativeDays?: number;
  } = {}
): string {
  const {showRelativeTime = true, maxRelativeDays = 7} = options;

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (showRelativeTime && diffInDays <= maxRelativeDays) {
      return formatRecentRelativeTime(diffInMs, diffInDays);
    }

    return formatDateToUserTimezone(dateString);
  } catch (error) {
    console.warn('Error formatting relative date:', error);
    return formatDateToUserTimezone(dateString);
  }
}

/**
 * Get timezone display name for the user's current timezone
 * @returns A user-friendly timezone name (e.g., "Eastern Standard Time", "Greenwich Mean Time")
 */
export function getUserTimezoneDisplayName(): string {
  try {
    const timezone = getUserTimezone();
    const now = new Date();

    // Get the timezone abbreviation/name
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'long',
    });

    const parts = formatter.formatToParts(now);
    const timeZoneName = parts.find(
      (part) => part.type === 'timeZoneName'
    )?.value;

    return timeZoneName ?? timezone;
  } catch {
    return 'Local Time';
  }
}
